import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Star, Music, Video, Mic2, Users, Search, Plus, Trash2, 
  Loader2, CheckCircle, ArrowLeft, Crown, Eye, X,
  Calendar, User, Radio, Camera, Headphones, Building2,
  Play, ExternalLink, Sparkles, Megaphone, CalendarDays,
  Heart, HandHeart, Zap, Youtube, AlertCircle, Link as LinkIcon, Share2, Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { 
  fetchYouTubeMetadata, 
  isValidYouTubeUrl, 
  extractYouTubeVideoId,
  getYouTubeEmbedUrl 
} from '../utils/youtubeUtils';



// Feature types with icons and descriptions
const FEATURE_TYPES = [
  { id: 'featured_artist', label: 'Featured Artist', icon: User, color: 'text-purple-500', description: 'Spotlight a creator on the homepage' },
  { id: 'featured_video', label: 'Featured Video', icon: Video, color: 'text-red-500', description: 'Highlight a video in the feed' },
  { id: 'featured_track', label: 'Featured Track', icon: Music, color: 'text-green-500', description: 'Feature a song or beat' },
  { id: 'featured_interview', label: 'Featured Interview', icon: Mic2, color: 'text-blue-500', description: 'Showcase an interview or podcast' },
  { id: 'youtube_embed', label: 'YouTube Video', icon: Youtube, color: 'text-red-500', description: 'Embed a YouTube video (interviews, etc.)' },
  { id: 'community_spotlight', label: 'Community Spotlight', icon: HandHeart, color: 'text-orange-500', description: 'Feature a nonprofit or organization' },
  { id: 'event_of_week', label: 'Event of the Week', icon: CalendarDays, color: 'text-pink-500', description: 'Highlight an upcoming event' },
  { id: 'new_release', label: 'New Release', icon: Sparkles, color: 'text-yellow-500', description: 'Announce a new release' },
  { id: 'announcement', label: 'Platform Announcement', icon: Megaphone, color: 'text-cyan-500', description: 'Important platform updates' }
];

// Display options
const DISPLAY_OPTIONS = [
  { id: 'show_home', label: 'Show on Home Page', default: true },
  { id: 'show_carousel', label: 'Show in Featured Carousel', default: true },
  { id: 'pin_top', label: 'Pin to Top of Landing Page', default: false },
  { id: 'highlight_spotlight', label: "This Week's Spotlight", default: false },
  { id: 'community_row', label: 'Add to Community Spotlight Row', default: false }
];

export default function AdminFeaturedPage() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders, isPlatformOwner } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [featuredContent, setFeaturedContent] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Builder state
  const [selectedType, setSelectedType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [displayOptions, setDisplayOptions] = useState(
    DISPLAY_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.id]: opt.default }), {})
  );
  
  // YouTube embed state
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeMetadata, setYoutubeMetadata] = useState(null);
  const [fetchingYoutube, setFetchingYoutube] = useState(false);
  const [youtubeError, setYoutubeError] = useState('');
  
  const [formData, setFormData] = useState({
    headline: '',
    description: '',
    ctaText: 'View Now',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    autoRemove: true
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Platform owner always has access
    if (!isPlatformOwner) {
      // Check for collateral duty permission
      const hasPermission = user?.collateralDuties?.featured_content_manager;
      if (!hasPermission) {
        toast.error('Not authorized');
        navigate('/feed');
        return;
      }
    }
    fetchFeaturedContent();
  }, [isAuthenticated, authLoading, user, isPlatformOwner]);

  const fetchFeaturedContent = async () => {
    try {
      const res = await fetch(`${API}/admin/featured/all`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Only show active featured content
        const activeContent = (data.featured || []).filter(item => item.active !== false);
        setFeaturedContent(activeContent);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  const handleSearch = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      let endpoint = '';
      
      // Different search endpoints based on content type
      switch (selectedType) {
        case 'featured_artist':
        case 'new_release':
          endpoint = `${API}/search/users?q=${encodeURIComponent(query)}&roles=artist,producer,podcaster,streamer,videographer,engineer,graphic_designer,studio&limit=10`;
          break;
        case 'featured_video':
        case 'featured_interview':
          endpoint = `${API}/search/videos?q=${encodeURIComponent(query)}&limit=10`;
          break;
        case 'featured_track':
          endpoint = `${API}/search/tracks?q=${encodeURIComponent(query)}&limit=10`;
          break;
        case 'community_spotlight':
          endpoint = `${API}/organizations/spotlight?search=${encodeURIComponent(query)}&all=true&limit=10`;
          break;
        case 'event_of_week':
          endpoint = `${API}/events?search=${encodeURIComponent(query)}&limit=10`;
          break;
        default:
          endpoint = `${API}/search?q=${encodeURIComponent(query)}&limit=10`;
      }
      
      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        // Normalize results based on type
        let results = [];
        if (selectedType === 'community_spotlight') {
          results = (data.organizations || []).map(org => ({
            id: org.org_id,
            type: 'organization',
            name: org.name,
            handle: org.handle,
            image: org.profile_image_url,
            subtitle: org.neighborhood || org.city,
            role: org.org_type
          }));
        } else if (selectedType === 'event_of_week') {
          results = (data.events || []).map(evt => ({
            id: evt.event_id,
            type: 'event',
            name: evt.title,
            image: evt.image_url,
            subtitle: `${evt.date} - ${evt.neighborhood || evt.city}`,
            date: evt.date
          }));
        } else if (selectedType.includes('video') || selectedType === 'featured_interview') {
          results = (data.videos || data.results || []).map(v => ({
            id: v.video_id,
            type: 'video',
            name: v.title,
            image: v.thumbnail_url,
            subtitle: v.creator_name || v.artist_name,
            views: v.views
          }));
        } else if (selectedType === 'featured_track') {
          results = (data.tracks || data.results || []).map(t => ({
            id: t.track_id,
            type: 'track',
            name: t.title,
            image: t.cover_url || t.artwork_url,
            subtitle: t.artist_name,
            plays: t.plays
          }));
        } else {
          // Artists/Users
          results = (data.users || data.results || []).map(u => ({
            id: u.user_id,
            type: 'user',
            name: u.name || u.artist_name,
            handle: u.handle,
            image: u.avatar_url || u.profile_image_url,
            subtitle: u.city || u.bio?.substring(0, 50),
            role: u.role
          }));
        }
        setSearchResults(results);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }, [selectedType, getAuthHeaders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && selectedType) {
        handleSearch(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedType, handleSearch]);

  // Handle YouTube URL fetch
  const handleFetchYouTube = async () => {
    if (!youtubeUrl.trim()) {
      setYoutubeError('Please enter a YouTube URL');
      return;
    }
    
    if (!isValidYouTubeUrl(youtubeUrl)) {
      setYoutubeError('Invalid YouTube URL. Please use a full URL (youtube.com/watch?v=...) or short URL (youtu.be/...)');
      return;
    }
    
    setFetchingYoutube(true);
    setYoutubeError('');
    setYoutubeMetadata(null);
    
    try {
      const metadata = await fetchYouTubeMetadata(youtubeUrl);
      
      if (metadata.error) {
        setYoutubeError(metadata.error);
        return;
      }
      
      setYoutubeMetadata(metadata);
      
      // Auto-fill the form with YouTube data
      setFormData(prev => ({
        ...prev,
        headline: metadata.title,
        description: `From ${metadata.channelName}`
      }));
      
      // Set selected content with YouTube data
      setSelectedContent({
        id: metadata.videoId,
        type: 'youtube',
        name: metadata.title,
        image: metadata.thumbnailUrl,
        subtitle: metadata.channelName,
        embedUrl: metadata.embedUrl,
        originalUrl: metadata.originalUrl
      });
      
      toast.success('YouTube video loaded!');
    } catch (error) {
      console.error('[YouTube] Fetch error:', error);
      setYoutubeError('Failed to fetch video information. Please try again.');
    } finally {
      setFetchingYoutube(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedType) {
      toast.error('Please select a feature type');
      return;
    }
    
    // For YouTube embed, check if we have metadata
    if (selectedType === 'youtube_embed') {
      if (!youtubeMetadata) {
        toast.error('Please fetch a YouTube video first');
        return;
      }
    } else if (!selectedContent && selectedType !== 'announcement') {
      toast.error('Please select content to feature');
      return;
    }
    
    if (!formData.headline) {
      toast.error('Please add a headline');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        type: selectedType,
        content_id: selectedContent?.id,
        content_type: selectedContent?.type || (selectedType === 'youtube_embed' ? 'youtube' : 'unknown'),
        title: formData.headline,
        description: formData.description,
        cta_text: formData.ctaText,
        thumbnail_url: selectedContent?.image || youtubeMetadata?.thumbnailUrl || '',
        display_options: displayOptions,
        start_date: formData.startDate,
        end_date: formData.endDate || null,
        auto_remove: formData.autoRemove,
        // YouTube-specific fields
        youtube_url: youtubeMetadata?.originalUrl || null,
        youtube_embed_url: youtubeMetadata?.embedUrl || null,
        youtube_video_id: youtubeMetadata?.videoId || null,
        youtube_channel: youtubeMetadata?.channelName || null
      };
      
      const res = await fetch(`${API}/admin/featured/set`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <div>
              <p className="font-medium">Saved to Homepage ✅</p>
              <p className="text-xs text-muted-foreground">
                {result.message || 'Featured content published!'}
              </p>
            </div>
          </div>,
          { duration: 5000 }
        );
        resetBuilder();
        fetchFeaturedContent();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to publish');
      }
    } catch (err) {
      toast.error('Failed to publish featured content');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (contentId) => {
    if (!confirm('Remove this featured content?')) return;
    
    try {
      const res = await fetch(`${API}/admin/featured/${contentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Featured content removed');
        fetchFeaturedContent();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove');
      }
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const resetBuilder = () => {
    setShowBuilder(false);
    setSelectedType('');
    setSelectedContent(null);
    setSearchQuery('');
    setSearchResults([]);
    setDisplayOptions(DISPLAY_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.id]: opt.default }), {}));
    // Reset YouTube state
    setYoutubeUrl('');
    setYoutubeMetadata(null);
    setYoutubeError('');
    setFormData({
      headline: '',
      description: '',
      ctaText: 'View Now',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      autoRemove: true
    });
  };

  // Quick feature actions
  const handleQuickFeature = async (action) => {
    toast.info(`Quick feature: ${action} - Coming soon!`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-featured-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin/control-center" className="text-muted-foreground hover:text-foreground flex items-center gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Admin
            </Link>
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-yellow-500" />
              <h1 className="font-heading text-3xl font-bold">Featured Content</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Curate and manage homepage featured content
            </p>
          </div>
          <Button onClick={() => setShowBuilder(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Featured Content
          </Button>
        </div>

        {/* Quick Feature Buttons */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button variant="outline" size="sm" onClick={() => handleQuickFeature('top_video')} className="gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            Feature Top Video This Week
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickFeature('top_track')} className="gap-2">
            <Music className="w-4 h-4 text-green-500" />
            Feature #1 Track
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickFeature('random_artist')} className="gap-2">
            <Users className="w-4 h-4 text-purple-500" />
            Feature Random Local Artist
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleQuickFeature('nonprofit')} className="gap-2">
            <HandHeart className="w-4 h-4 text-orange-500" />
            Nonprofit of the Week
          </Button>
        </div>

        {/* Current Featured Content */}
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-bold">Currently Featured</h2>
          
          {featuredContent.length === 0 ? (
            <Card className="glass-card border-border p-12 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">No Featured Content</h3>
              <p className="text-muted-foreground mb-4">
                Add your first featured content to highlight on the homepage.
              </p>
              <Button onClick={() => setShowBuilder(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Featured Content
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {featuredContent.map((content) => (
                <FeaturedContentCard 
                  key={content.id || content.type} 
                  content={content} 
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}
        </div>

        {/* Featured Content Builder Modal */}
        <AnimatePresence>
          {showBuilder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={(e) => e.target === e.currentTarget && resetBuilder()}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-background border border-border rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              >
                <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Add Featured Content</h2>
                  <Button variant="ghost" size="sm" onClick={resetBuilder}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="p-6 space-y-8">
                  {/* Section 1: Feature Type */}
                  <div>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">1</span>
                      Select Feature Type
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {FEATURE_TYPES.map((type) => (
                        <button
                          key={type.id}
                          onClick={() => {
                            setSelectedType(type.id);
                            setSelectedContent(null);
                            setSearchQuery('');
                            setSearchResults([]);
                          }}
                          className={`p-4 rounded-lg border text-left transition-all ${
                            selectedType === type.id 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-border hover:bg-muted/30'
                          }`}
                        >
                          <type.icon className={`w-6 h-6 mb-2 ${type.color}`} />
                          <div className="font-medium text-sm">{type.label}</div>
                          <div className="text-xs text-muted-foreground mt-1">{type.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Section 2: Pick Content */}
                  {selectedType && selectedType !== 'announcement' && selectedType !== 'youtube_embed' && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">2</span>
                        Pick Content
                      </h3>
                      
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder={`Search ${FEATURE_TYPES.find(t => t.id === selectedType)?.label.toLowerCase()}s...`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 bg-muted/30 border-border"
                        />
                        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
                      </div>

                      {/* Selected Content Display */}
                      {selectedContent && (
                        <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 mb-4 flex items-center gap-4">
                          {selectedContent.image ? (
                            <img src={selectedContent.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-muted/50 flex items-center justify-center">
                              <User className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="font-bold">{selectedContent.name}</div>
                            {selectedContent.handle && <div className="text-sm text-muted-foreground">{selectedContent.handle ? `@${selectedContent.handle}` : ""}</div>}
                            {selectedContent.subtitle && <div className="text-sm text-muted-foreground">{selectedContent.subtitle}</div>}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedContent(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* Search Results */}
                      {searchResults.length > 0 && !selectedContent && (
                        <div className="border border-border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                          {searchResults.map((result) => (
                            <button
                              key={result.id}
                              onClick={() => {
                                setSelectedContent(result);
                                setFormData(prev => ({ ...prev, headline: result.name }));
                              }}
                              className="w-full p-3 flex items-center gap-3 hover:bg-muted/30 border-b border-border last:border-b-0 text-left"
                            >
                              {result.image ? (
                                <img src={result.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center">
                                  <User className="w-5 h-5 text-muted-foreground" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{result.name}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {result.handle && `@${result.handle} • `}
                                  {result.subtitle}
                                </div>
                              </div>
                              {result.role && (
                                <Badge variant="outline" className="capitalize text-xs">{result.role.replace('_', ' ')}</Badge>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Section 2: YouTube URL Input (for youtube_embed type) */}
                  {selectedType === 'youtube_embed' && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">2</span>
                        Paste YouTube URL
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                            <Input
                              placeholder="https://www.youtube.com/watch?v=... or youtu.be/..."
                              value={youtubeUrl}
                              onChange={(e) => {
                                setYoutubeUrl(e.target.value);
                                setYoutubeError('');
                              }}
                              className="pl-10 bg-muted/30 border-border"
                              data-testid="youtube-url-input"
                            />
                          </div>
                          <Button 
                            onClick={handleFetchYouTube}
                            disabled={fetchingYoutube || !youtubeUrl.trim()}
                            data-testid="fetch-youtube-btn"
                          >
                            {fetchingYoutube ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <LinkIcon className="w-4 h-4 mr-2" />
                                Fetch
                              </>
                            )}
                          </Button>
                        </div>
                        
                        {/* YouTube Error */}
                        {youtubeError && (
                          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {youtubeError}
                          </div>
                        )}
                        
                        {/* YouTube Preview */}
                        {youtubeMetadata && (
                          <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 space-y-4">
                            <div className="flex items-start gap-4">
                              <div className="w-32 h-20 rounded-lg overflow-hidden bg-black flex-shrink-0">
                                <img 
                                  src={youtubeMetadata.thumbnailUrl} 
                                  alt={youtubeMetadata.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = youtubeMetadata.thumbnailMaxRes;
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm line-clamp-2">{youtubeMetadata.title}</div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  <span className="text-red-400">Channel:</span> {youtubeMetadata.channelName}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    <Youtube className="w-3 h-3 mr-1 text-red-500" />
                                    YouTube
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    ID: {youtubeMetadata.videoId}
                                  </Badge>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setYoutubeMetadata(null);
                                  setSelectedContent(null);
                                  setYoutubeUrl('');
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {/* Embedded Video Preview */}
                            <div className="rounded-lg overflow-hidden bg-black aspect-video">
                              <iframe
                                src={youtubeMetadata.embedUrl}
                                title={youtubeMetadata.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              />
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center justify-between text-sm">
                              <a 
                                href={youtubeMetadata.originalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-foreground flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Open on YouTube
                              </a>
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                Video verified & ready to embed
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Help text */}
                        {!youtubeMetadata && !youtubeError && (
                          <p className="text-xs text-muted-foreground">
                            Paste any YouTube URL (full link or short youtu.be link). The video title, thumbnail, and channel name will be auto-fetched.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section 3: Display Options */}
                  {selectedType && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">3</span>
                        Display Options
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {DISPLAY_OPTIONS.map((option) => (
                          <div key={option.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                            <Label htmlFor={option.id} className="text-sm cursor-pointer">{option.label}</Label>
                            <Switch
                              id={option.id}
                              checked={displayOptions[option.id]}
                              onCheckedChange={(checked) => setDisplayOptions(prev => ({ ...prev, [option.id]: checked }))}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section 4: Text Options */}
                  {selectedType && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">4</span>
                        Content Details
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <Label>Headline *</Label>
                          <Input
                            placeholder="Short title shown to users"
                            value={formData.headline}
                            onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                            className="mt-1 bg-muted/30 border-border"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea
                            placeholder="1-2 sentences about this featured content"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            className="mt-1 bg-muted/30 border-border"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label>CTA Button Text</Label>
                          <Select value={formData.ctaText} onValueChange={(v) => setFormData(prev => ({ ...prev, ctaText: v }))}>
                            <SelectTrigger className="mt-1 bg-muted/30 border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="View Now">View Now</SelectItem>
                              <SelectItem value="Watch Now">Watch Now</SelectItem>
                              <SelectItem value="Listen Now">Listen Now</SelectItem>
                              <SelectItem value="View Profile">View Profile</SelectItem>
                              <SelectItem value="Learn More">Learn More</SelectItem>
                              <SelectItem value="Get Tickets">Get Tickets</SelectItem>
                              <SelectItem value="Support">Support</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 5: Duration */}
                  {selectedType && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">5</span>
                        Feature Duration
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                            className="mt-1 bg-muted/30 border-border"
                          />
                        </div>
                        <div>
                          <Label>End Date (optional)</Label>
                          <Input
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                            className="mt-1 bg-muted/30 border-border"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 p-3 rounded-lg bg-muted/30 border border-border">
                        <Label htmlFor="auto-remove" className="text-sm cursor-pointer">Auto-remove when expired</Label>
                        <Switch
                          id="auto-remove"
                          checked={formData.autoRemove}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, autoRemove: checked }))}
                        />
                      </div>
                    </div>
                  )}

                  {/* Section 6: Preview */}
                  {selectedType && (selectedContent || selectedType === 'announcement' || youtubeMetadata) && (
                    <div>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">6</span>
                        Preview
                      </h3>
                      <Card className="glass-card border-border overflow-hidden">
                        {/* YouTube Video Embed Preview */}
                        {selectedType === 'youtube_embed' && youtubeMetadata ? (
                          <>
                            <div className="aspect-video bg-black">
                              <iframe
                                src={youtubeMetadata.embedUrl}
                                title={youtubeMetadata.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                              />
                            </div>
                            <div className="p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className="bg-red-500 text-white">
                                  <Youtube className="w-3 h-3 mr-1" />
                                  YouTube
                                </Badge>
                              </div>
                              <h4 className="font-heading font-bold text-lg">{formData.headline || youtubeMetadata.title}</h4>
                              {formData.description && (
                                <p className="text-sm text-muted-foreground mt-1">{formData.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-4">
                                <Button size="sm">
                                  {formData.ctaText}
                                </Button>
                                <Button variant="outline" size="sm" className="gap-1">
                                  <Share2 className="w-3 h-3" />
                                  Share
                                </Button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="relative h-48 bg-gradient-to-br from-white/10 to-white/5">
                              {selectedContent?.image && (
                                <img src={selectedContent.image} alt="" className="w-full h-full object-cover" />
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                              <div className="absolute top-4 left-4">
                                <Badge className="bg-primary text-primary-foreground">
                                  {FEATURE_TYPES.find(t => t.id === selectedType)?.label}
                                </Badge>
                              </div>
                            </div>
                            <div className="p-4">
                              <h4 className="font-heading font-bold text-lg">{formData.headline || 'Headline'}</h4>
                              {formData.description && (
                                <p className="text-sm text-muted-foreground mt-1">{formData.description}</p>
                              )}
                              <Button className="mt-4" size="sm">
                                {formData.ctaText}
                              </Button>
                            </div>
                          </>
                        )}
                      </Card>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="sticky bottom-0 bg-background border-t border-border p-4 flex items-center justify-end gap-3">
                  <Button variant="outline" onClick={resetBuilder}>Cancel</Button>
                  <Button 
                    onClick={handlePublish} 
                    disabled={saving || !selectedType || (!selectedContent && selectedType !== 'announcement' && selectedType !== 'youtube_embed') || (selectedType === 'youtube_embed' && !youtubeMetadata)}
                    className="gap-2"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Publish Featured Content
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function FeaturedContentCard({ content, onRemove }) {
  const typeConfig = FEATURE_TYPES.find(t => t.id === content.type) || FEATURE_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const isYouTube = content.type === 'youtube_embed' || content.youtube_embed_url;
  
  // Get visual thumbnail with fallbacks
  const getThumbnail = () => {
    if (content.thumbnail_url) return content.thumbnail_url;
    if (content.video?.thumbnail_url) return content.video.thumbnail_url;
    if (content.track?.cover_art_url) return content.track.cover_art_url;
    if (content.track?.cover_url) return content.track.cover_url;
    if (content.artist?.avatar_url) return content.artist.avatar_url;
    if (content.user?.avatar_url) return content.user.avatar_url;
    return null;
  };
  
  const thumbnail = getThumbnail();
  const createdAt = content.created_at ? new Date(content.created_at).toLocaleString() : null;
  
  return (
    <Card className="glass-card border-border p-4 relative overflow-hidden" data-testid="featured-content-card">
      {/* Saved to Homepage indicator */}
      {content.active && (
        <div className="absolute top-0 right-0 bg-green-500/20 text-green-400 text-xs px-3 py-1 rounded-bl-lg flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          <span>Live on Homepage</span>
        </div>
      )}
      
      <div className="flex items-center gap-4">
        {/* Visual Preview */}
        {thumbnail ? (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-border">
            <img src={thumbnail} alt="" className="w-full h-full object-cover" />
            {isYouTube && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Play className="w-8 h-8 text-white" />
              </div>
            )}
            {content.type === 'featured_artist' && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full bg-gradient-to-t from-purple-500/50 to-transparent" />
              </div>
            )}
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center flex-shrink-0 border border-border">
            <TypeIcon className={`w-10 h-10 ${typeConfig.color}`} />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className={`${typeConfig.color.replace('text-', 'bg-')}/20 ${typeConfig.color}`}>
              <TypeIcon className="w-3 h-3 mr-1" />
              {typeConfig.label}
            </Badge>
            {content.active && <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>}
            {isYouTube && content.youtube_channel && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                {content.youtube_channel}
              </Badge>
            )}
          </div>
          <h3 className="font-bold truncate text-lg">{content.title || 'Untitled'}</h3>
          
          {/* Show artist/content info */}
          {content.artist?.stage_name && (
            <p className="text-sm text-purple-400">{content.artist.stage_name}</p>
          )}
          {content.track?.title && content.type !== 'top_song' && (
            <p className="text-sm text-green-400">🎵 {content.track.title}</p>
          )}
          {content.video?.title && content.type !== 'top_video' && (
            <p className="text-sm text-blue-400">🎬 {content.video.title}</p>
          )}
          
          {content.description && (
            <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{content.description}</p>
          )}
          
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
            {createdAt && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Saved: {createdAt}
              </span>
            )}
            {content.start_date && <span>Starts: {content.start_date}</span>}
            {content.end_date && <span>Ends: {content.end_date}</span>}
            {content.youtube_url && (
              <a 
                href={content.youtube_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                YouTube
              </a>
            )}
            {content.artist_handle && (
              <Link 
                to={`/u/${content.artist_handle}`}
                className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                @{content.artist_handle}
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm">
            <Eye className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onRemove(content.featured_id || content.id || content.type)} className="text-red-500 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
